import { ScrollText } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, getApiErrorMessage, getApiErrorStatus } from '@/shared/api/axios';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Button } from '@/shared/components/ui/Button';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR } from '@/shared/utils/currency';
import { Calendar, RefreshCw, Download, AlertCircle, ChevronDown } from 'lucide-react';

interface Gstr1Record {
  id: string;
  description: string;
  igst: number;
  cgst: number;
  sgst: number;
  taxable: number;
}

/** Money cell of a statutory return: mono, tabular, right-aligned, sign preserved. */
const GST_FIGURE = 'px-5 py-2.5 text-right font-mono tabular-nums text-data-md';

export function Gstr1SummaryReport() {
  const [period, setPeriod] = useState('Jul 2026');
  const [isQuarterly, setIsQuarterly] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const { data: resData, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['accounts', 'reports', 'gstr-1', period, isQuarterly],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('period', period);
      if (isQuarterly) params.append('isQuarterly', 'true');
      const { data } = await api.get(`/api/accounts/reports/gstr-1?${params.toString()}`);
      return data.data;
    }
  });

  const records: Gstr1Record[] = resData?.records || [];
  // A fixed date string used to stand in for real metadata, so a report built
  // minutes ago could claim it was generated on 06 Aug 2026 at 00:02. Report
  // "not reported" instead of inventing provenance for a statutory document.
  const lastUpdated = resData?.meta?.lastUpdated;

  // Sum of the rows the server actually returned for this period — the return's
  // own figure, not a projected or rounded one.
  const totals = records.reduce(
    (acc, r) => ({
      igst: acc.igst + (Number(r.igst) || 0),
      cgst: acc.cgst + (Number(r.cgst) || 0),
      sgst: acc.sgst + (Number(r.sgst) || 0),
      taxable: acc.taxable + (Number(r.taxable) || 0),
    }),
    { igst: 0, cgst: 0, sgst: 0, taxable: 0 },
  );

  const handleDownloadExcel = () => {
    if (!records.length) return;
    const rows = [
      ['Description', 'IGST Amount', 'CGST Amount', 'SGST Amount', 'Taxable Amount'],
      ...records.map(r => [
        `"${r.description}"`,
        r.igst,
        r.cgst,
        r.sgst,
        r.taxable
      ])
    ];

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GSTR_1_Summary_${period.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  return (
    <div className="space-y-5">
      {/* ── Top Header Bar ─────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <PageHeader icon={ScrollText} eyebrow="GST & Compliance" title="GSTR-1 Summary" description="Outward supplies by section for the selected period, ready for export." />

        <div className="flex items-center gap-3">
          <div className="relative">
            <Button
              onClick={() => setShowExportMenu(!showExportMenu)}
              aria-expanded={showExportMenu}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
              <ChevronDown className="w-3.5 h-3.5 opacity-80" />
            </Button>

            {showExportMenu && (
              <div className="absolute right-0 z-50 mt-2 w-52 rounded-card-sm border border-border bg-popover py-1 text-body-sm shadow-surface-2">
                <button
                  onClick={handleDownloadExcel}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left font-medium text-popover-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Download className="h-4 w-4 text-on-surface-variant" />
                  Excel / CSV spreadsheet
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Filing-scheme notice ─────────────────────────────────────────────── */}
      <div className="flex flex-col items-start justify-between gap-3 rounded-card border border-info/20 bg-info/10 p-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-4 w-4 shrink-0 text-info-text" />
          <p className="text-body-sm text-info-text">
            {isQuarterly
              ? 'Showing the quarterly summary (QRMP scheme active). Switch back to monthly if you file GSTR-1 monthly.'
              : 'Choose monthly or quarterly here to match the scheme on your GST registration.'}
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => setIsQuarterly(!isQuarterly)}
          className="h-8 shrink-0 px-4 text-body-sm font-medium"
        >
          {isQuarterly ? 'Switch to Monthly' : 'Switch to Quarterly'}
        </Button>
      </div>

      {/* ── Period & refresh controls ──────────────────────────────────────── */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div className="flex flex-col">
          <label htmlFor="gstr1-period" className="mb-1 text-label-sm uppercase tracking-wider text-on-surface-variant">Reporting Period</label>
          <div className="relative">
            <select
              id="gstr1-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-52 rounded-lg border border-input bg-background py-2 pl-3 pr-9 text-body-md font-medium text-on-surface shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="Jul 2026">Jul 2026</option>
              <option value="Jun 2026">Jun 2026</option>
              <option value="May 2026">May 2026</option>
              <option value="Q1 FY 2026-27">Q1 FY 2026-27</option>
            </select>
            <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-body-sm text-on-surface-subtle">
            Last updated{' '}
            <strong className="font-semibold text-on-surface-variant">{lastUpdated || 'not reported by the server'}</strong>
          </span>

          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            className="h-8 gap-1.5 px-3 text-body-sm font-medium"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin motion-reduce:animate-none' : ''}`} />
            Refresh report
          </Button>
        </div>
      </div>

      {/* ── GSTR-1 records table ───────────────────────────────────────────── */}
      {isLoading ? (
        <div className="space-y-2 rounded-card border border-border bg-card p-4 shadow-xs" aria-busy="true" aria-live="polite">
          <Skeleton className="h-8 w-full" />
          {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : isError ? (
        <div role="alert" className={`rounded-card border p-4 text-sm shadow-xs ${getApiErrorStatus(error) === 403 ? 'border-warning/25 bg-warning/10 text-warning-text' : 'border-danger/20 bg-danger/10 text-danger-text'}`}>
          <p className="font-semibold">{getApiErrorStatus(error) === 403 ? 'Permission denied' : 'Could not generate GSTR-1 Summary'}</p>
          <p className="mt-1 text-body-sm">{getApiErrorStatus(error) === 403
            ? 'GST returns are restricted to the Accounts department or an administrator. No figures are shown.'
            // `/api/accounts/*` answers with { error: '<string>' } — this reads it
            : getApiErrorMessage(error, `No GSTR-1 data came back for ${period}. Check the period, then refresh.`)}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : records.length === 0 ? (
        <div className="rounded-card border border-border bg-card shadow-xs">
          <EmptyState
            title={`No outward supplies reported for ${period}`}
            description={isQuarterly
              ? 'This quarter has no GSTR-1 outward-supply lines yet. Finalise the invoices in the period, then refresh.'
              : 'This period has no GSTR-1 outward-supply lines yet. Finalise the invoices in the period, then refresh.'}
            action={<Button variant="outline" size="sm" onClick={() => refetch()}>Refresh report</Button>}
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs">
          <div className="border-b border-border px-5 py-2.5">
            <p className="text-body-sm text-on-surface-variant">
              <strong className="font-mono tabular-nums font-semibold text-on-surface">{records.length}</strong> supply line{records.length === 1 ? '' : 's'} in {period}{isQuarterly ? ' (quarterly)' : ''}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-body-md text-left border-collapse">
              <thead className="border-b border-border bg-muted text-label-caps uppercase text-on-surface-variant">
                <tr>
                  <th className="px-5 py-2.5 min-w-[320px]">Description</th>
                  <th className="px-5 py-2.5 text-right w-36">IGST</th>
                  <th className="px-5 py-2.5 text-right w-36">CGST</th>
                  <th className="px-5 py-2.5 text-right w-36">SGST</th>
                  <th className="px-5 py-2.5 text-right w-40">Taxable</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {records.map((row) => (
                  <tr key={row.id} className="hover:bg-muted/50">
                    <td className="px-5 py-2.5 leading-relaxed text-on-surface">{row.description}</td>
                    <td className={`${GST_FIGURE} ${row.igst < 0 ? 'text-destructive' : ''}`}>{row.igst == null ? '—' : formatINR(row.igst)}</td>
                    <td className={`${GST_FIGURE} ${row.cgst < 0 ? 'text-destructive' : ''}`}>{row.cgst == null ? '—' : formatINR(row.cgst)}</td>
                    <td className={`${GST_FIGURE} ${row.sgst < 0 ? 'text-destructive' : ''}`}>{row.sgst == null ? '—' : formatINR(row.sgst)}</td>
                    <td className={`${GST_FIGURE} font-semibold ${row.taxable < 0 ? 'text-destructive' : 'text-on-surface'}`}>{row.taxable == null ? '—' : formatINR(row.taxable)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/50">
                  <td className="px-5 py-3 text-right">
                    <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Period totals</span>
                  </td>
                  <td className={GST_FIGURE}>{formatINR(totals.igst)}</td>
                  <td className={GST_FIGURE}>{formatINR(totals.cgst)}</td>
                  <td className={GST_FIGURE}>{formatINR(totals.sgst)}</td>
                  <td className={`${GST_FIGURE} font-semibold`}>{formatINR(totals.taxable)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Reconciliation signpost — no figures here, only what the totals are and are not. */}
          <p className="border-t border-border px-5 py-3 text-body-sm text-on-surface-variant">
            These totals sum the lines above for this period only. Reconcile them against GSTR-3B and your purchase
            register before filing; a difference here is not corrected by this report.
          </p>
        </div>
      )}
    </div>
  );
}
